import React, { useState, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import { HiMenu, HiUpload, HiClipboardCopy, HiPhotograph, HiVideoCamera, HiVolumeUp } from 'react-icons/hi';
import { uploadFile } from '../../services/assetService';
import Toast from '../../components/toaster';

const UploadImage = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploadedUrl, setUploadedUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [fileType, setFileType] = useState(''); // 'image', 'video', 'audio'

    // Refs for media elements to pause them when needed
    const audioRef = useRef(null);
    const videoRef = useRef(null);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validation for 5MB
            if (file.size > 5 * 1024 * 1024) {
                setError('File size must be less than 5MB');
                Toast.error('File size must be less than 5MB');
                return;
            }

            let type = '';
            if (file.type.startsWith('image/')) type = 'image';
            else if (file.type.startsWith('video/')) type = 'video';
            else if (file.type.startsWith('audio/')) type = 'audio';
            else {
                setError('Please select an image, video, or audio file');
                return;
            }

            setFileType(type);
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
            setError('');
            setUploadedUrl(''); // Reset previous upload
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setUploading(true);
        setError('');

        try {
            const url = await uploadFile(selectedFile);
            setUploadedUrl(url);
            Toast.success(`${fileType.charAt(0).toUpperCase() + fileType.slice(1)} uploaded successfully`);
        } catch (err) {
            console.error('Upload failed:', err);
            setError(err.message || 'Failed to upload file');
            Toast.error(err.message || 'Failed to upload file');
        } finally {
            setUploading(false);
        }
    };

    const copyToClipboard = () => {
        if (uploadedUrl) {
            navigator.clipboard.writeText(uploadedUrl);
            Toast.success('URL copied to clipboard');
        }
    };

    const renderPreview = () => {
        if (!previewUrl) return null;

        if (fileType === 'image') {
            return (
                <div className="relative w-full h-36 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                    <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-full h-full object-contain"
                    />
                </div>
            );
        } else if (fileType === 'video') {
            return (
                <div className="relative w-full h-48 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                    <video
                        ref={videoRef}
                        src={previewUrl}
                        controls
                        className="w-full h-full object-contain"
                    />
                </div>
            );
        } else if (fileType === 'audio') {
            return (
                <div className="relative w-full p-4 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center">
                    <audio
                        ref={audioRef}
                        src={previewUrl}
                        controls
                        className="w-full"
                    />
                </div>
            );
        }
        return null;
    };

    const getIconForType = () => {
        if (fileType === 'video') return <HiVideoCamera className="w-10 h-10 text-gray-400 mb-3" />;
        if (fileType === 'audio') return <HiVolumeUp className="w-10 h-10 text-gray-400 mb-3" />;
        return <HiPhotograph className="w-10 h-10 text-gray-400 mb-3" />;
    };

    return (
        <div className="h-screen flex overflow-hidden">
            <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
                <div className="flex-1 flex flex-col min-h-0 overflow-y-auto no-scrollbar">
                    {/* Header */}
                    <div className="mb-4 md:mb-6">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
                            >
                                <HiMenu className="w-6 h-6 text-gray-800" />
                            </button>
                            <h1 className="text-xl md:text-3xl font-bold text-gray-800">Upload Media</h1>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="max-w-lg mx-auto w-full space-y-6 mt-10">

                        {/* Upload Area */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">Select Media</h2>

                            <div className="space-y-4">
                                <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-brand transition-colors relative">
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                        {!selectedFile ? (
                                            <>
                                                <div className="flex gap-4 mb-3">
                                                    <HiPhotograph className="w-7 h-7 text-gray-400" />
                                                    <HiVideoCamera className="w-7 h-7 text-gray-400" />
                                                    <HiVolumeUp className="w-7 h-7 text-gray-400" />
                                                </div>
                                                <p className="mb-2 text-sm text-gray-500">
                                                    <span className="font-semibold">Click to upload</span> or drag and drop
                                                </p>
                                                <p className="text-xs text-gray-500">Supported media files</p>
                                            </>
                                        ) : (
                                            <>
                                                {getIconForType()}
                                                <p className="text-sm font-medium text-gray-700">{selectedFile.name}</p>
                                                <p className="text-xs text-gray-500 mt-1 capitalize">{fileType}</p>
                                                <span className="mt-2 text-xs text-brand font-medium">Click to change</span>
                                            </>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept="image/*,video/*,audio/*"
                                        onChange={handleFileSelect}
                                        disabled={uploading}
                                    />
                                </label>

                                {error && (
                                    <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
                                        {error}
                                    </div>
                                )}

                                {/* Preview */}
                                {selectedFile && (
                                    <div className="mt-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <p className="text-sm font-medium text-gray-700">Preview:</p>
                                            <button
                                                onClick={() => {
                                                    setSelectedFile(null);
                                                    setPreviewUrl(null);
                                                    setFileType('');
                                                    setUploadedUrl('');
                                                }}
                                                className="text-xs text-red-500 hover:text-red-700"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                        {renderPreview()}
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="flex justify-end pt-4">
                                    <button
                                        onClick={handleUpload}
                                        disabled={!selectedFile || uploading}
                                        className="px-6 py-2.5 bg-brand text-white text-sm font-medium rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                                    >
                                        {uploading ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <HiUpload className="w-4 h-4" />
                                                Upload {fileType ? fileType.charAt(0).toUpperCase() + fileType.slice(1) : 'File'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Result Area */}
                        {uploadedUrl && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-fade-in">
                                <div className="space-y-3">
                                    <label className="text-sm font-medium text-gray-700">{fileType.charAt(0).toUpperCase() + fileType.slice(1)} URL</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={uploadedUrl}
                                            readOnly
                                            className="flex-1 p-2.5 text-sm bg-gray-50 border border-gray-300 rounded-lg text-gray-600 focus:ring-brand focus:border-brand"
                                        />
                                        <button
                                            onClick={copyToClipboard}
                                            className="p-2.5 text-gray-500 hover:text-brand bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                                            title="Copy to clipboard"
                                        >
                                            <HiClipboardCopy className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UploadImage;
